import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import LanguageSelector from "./LanguageSelector";
import { Images } from "../assets";
import { useTranslation } from "react-i18next";

/** Desktop Dropdown (hover-based) */
const Dropdown = ({ label, items, activePaths }) => {
  const location = useLocation();
  const isActive = activePaths.includes(location.pathname);

  return (
    <div className="relative group">
      <span
        className={`cursor-pointer hover:text-orange ${
          isActive ? "text-orange" : "text-white"
        }`}
      >
        {label}
      </span>
      <div className="absolute left-1/2 transform -translate-x-1/2 top-full pt-5 pb-2 w-48 bg-headerFooter shadow-lg rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `block px-4 py-2 hover:text-orange ${
                isActive ? "text-orange" : "text-white"
              }`
            }
          >
            {item.label}
          </NavLink>
        ))}
      </div>
    </div>
  );
};

/** Desktop Navigation Button */
const NavButton = ({ to, children, external = false }) => {
  return external ? (
    // External Link (Opens in New Tab)
    <a href={to} target="_blank" rel="noopener noreferrer">
      <button className="border px-14 py-3 rounded border-white hover:text-orange text-white">
        {children}
      </button>
    </a>
  ) : (
    // Internal NavLink (Keeps Active State)
    <NavLink to={to}>
      {({ isActive }) => (
        <button
          className={`border px-14 py-3 rounded border-white hover:text-orange ${
            isActive ? "text-orange" : "text-white"
          }`}
        >
          {children}
        </button>
      )}
    </NavLink>
  );
};

/** Desktop Navigation Button with Icon */
const NavButtonWithIcon = ({ to, children, icon, iconClasses }) => (
  <NavLink to={to}>
    {({ isActive }) => (
      <div className="relative">
        <button
          className={`border px-14 py-3 rounded border-white hover:text-orange ${
            isActive ? "text-orange" : "text-white"
          }`}
        >
          {children}
        </button>
        {icon && (
          <img src={icon} alt="Icon" className={`absolute ${iconClasses}`} />
        )}
      </div>
    )}
  </NavLink>
);

/** Mobile Dropdown (click-to-expand) */
const MobileDropdown = ({ label, items, activePaths }) => {
  const location = useLocation();
  const isActive = activePaths.includes(location.pathname);
  const [open, setOpen] = useState(false);

  return (
    <div className="border-b border-gray-700">
      <button
        onClick={() => setOpen(!open)}
        className={`w-full text-left px-4 py-2 flex justify-between items-center ${
          isActive ? "text-orange" : "text-white"
        }`}
      >
        {label}
        <span>{open ? "-" : "+"}</span>
      </button>
      {open && (
        <div className="pl-4">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `block px-4 py-2 ${isActive ? "text-orange" : "text-white"}`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
};

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t } = useTranslation();

  return (
    <header className="sticky top-0 z-50 bg-black">
      <nav className="flex items-center justify-between max-w-full mx-5 p-4">
        {/* Logo */}
        <NavLink to="/">
          <img src={Images.MAIN_LOGO} alt="Logo" className="w-1/2 md:w-full" />
        </NavLink>

        {/* Desktop Navigation */}
        <div className="hidden xl:flex items-center gap-6">
          <LanguageSelector />

          <div className="flex bg-headerFooter rounded-lg gap-28 px-10 py-3 text-sm">
            <Dropdown
              label={t("header.productsServices")}
              activePaths={["/r&d", "/hydraulique", "/oxycoupage"]}
              items={[
                { to: "/hydraulique", label: t("header.hydraulique") },
                { to: "/oxycoupage", label: t("header.oxycoupage") },
                { to: "/r&d", label: t("header.rd") },
              ]}
            />

            <NavLink
              to="/nos-machines"
              className={({ isActive }) =>
                `hover:text-orange ${isActive ? "text-orange" : "text-white"}`
              }
            >
              {t("header.ourMachines")}
            </NavLink>

            <Dropdown
              label={t("header.aboutUs")}
              activePaths={[
                "/a-propos",
                "/notre-histoire",
                "/nos-realisations",
              ]}
              items={[
                { to: "/a-propos", label: t("header.whoWeAre") },
                { to: "/notre-histoire", label: t("header.ourHistory") },
                { to: "/nos-realisations", label: t("header.ourWorks") },
              ]}
            />
          </div>

          <div className="flex justify-between gap-6 text-sm">
            <NavButton to="https://parts.aficor.ch/fr/" external>
              {t("header.afishop")}
            </NavButton>
            <NavButtonWithIcon
              to="/contact"
              icon={Images.ORANGE_STAR}
              iconClasses="w-4 h-4 right-2 top-2"
            >
              {t("header.contact")}
            </NavButtonWithIcon>

            {!localStorage.getItem("user_id") || (
              <NavButton to="/admin/dashboard" external>
                {t("header.dashboard")}
              </NavButton>
            )}
          </div>
        </div>

        {/* Mobile Hamburger */}
        <div className="flex items-center gap-5 xl:hidden">
          <LanguageSelector />

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-white focus:outline-none"
          >
            {/* Hamburger icon (switches to an "X" when open) */}
            <svg
              className="w-8 h-8"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              {mobileMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="bg-black text-start xl:hidden">
          <div className="border-t border-gray-700">
            <MobileDropdown
              label={t("header.productsServices")}
              activePaths={["/r&d", "/hydraulique", "/oxycoupage"]}
              items={[
                { to: "/hydraulique", label: t("header.hydraulique") },
                { to: "/oxycoupage", label: t("header.oxycoupage") },
                { to: "/r&d", label: t("header.rd") },
              ]}
            />
            <NavLink
              to="/nos-machines"
              className={({ isActive }) =>
                (isActive ? "text-orange" : "text-white") +
                " block px-4 py-2 border-b border-gray-700"
              }
              onClick={() => setMobileMenuOpen(false)}
            >
              {t("header.ourMachines")}
            </NavLink>
            <MobileDropdown
              label={t("header.aboutUs")}
              activePaths={[
                "/a-propos",
                "/notre-histoire",
                "/nos-realisations",
              ]}
              items={[
                { to: "/a-propos", label: t("header.whoWeAre") },
                { to: "/notre-histoire", label: t("header.ourHistory") },
                { to: "/nos-realisations", label: t("header.ourWorks") },
              ]}
            />
            <a
              href="https://parts.aficor.ch/fr/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white block px-4 py-2 border-b border-gray-700"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t("header.afishop")}
            </a>
            <NavLink
              to="/contact"
              className={({ isActive }) =>
                (isActive ? "text-orange" : "text-white") +
                " block px-4 py-2 border-b border-gray-700"
              }
              onClick={() => setMobileMenuOpen(false)}
            >
              {t("header.contact")}
            </NavLink>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
